<template>
  <!-- Account Details -->
  <q-list dense>
    <!-- Profile Image -->
    <detail-item
      without-name
      without-icon
      class="q-mb-md"
    >
      <div class="flex">
        <profile-image
          size="75px"
          clickable
          additional-icon="mdi-camera"
          :error="!!errors['image.id'] ? errors['image.id'][0] : null"
          :initials="initials"
          :profile-image="user.image ? user.image.signed_thumbnail_url : null"
          :color="user.color"
          @click="!user.image ? $refs.fileSelectDialog.open() : undefined"
        >
          <q-menu v-if="user.image !== null">
            <q-card>
              <q-list padding :dense="!$q.platform.has.touch">
                <q-item-wrapper
                  v-close-popup
                  clickable
                  label="View Image"
                  icon="mdi-image-outline"
                  @click="$refs.fileSelectDialog.viewingFileID = user.image.id"
                />
                <q-item-wrapper
                  v-close-popup
                  clickable
                  label="Change Image"
                  icon="mdi-image-search-outline"
                  @click="$refs.fileSelectDialog.open()"
                />
                <q-item-wrapper
                  v-close-popup
                  clickable
                  label="Remove Image"
                  icon="mdi-close"
                  color="red-7"
                  @click="user.image = null"
                />
              </q-list>
            </q-card>
          </q-menu>
        </profile-image>
      </div>
    </detail-item>

    <!-- Name -->
    <detail-item
      :name-spacing="nameSpacing"
      with-input
      icon="mdi-account"
      name="Name"
      required
    >
      <div class="row q-col-gutter-sm">
        <div class="col-md-6 col-12">
          <q-input
            v-model="user.first_name"
            :error="!!errors['first_name']"
            :error-message="errors['first_name'] ? errors['first_name'][0] : null"
            outlined
            placeholder="First Name"
            dense
            hide-bottom-space
          />
        </div>
        <div class="col-md-6 col-12">
          <q-input
            v-model="user.last_name"
            :error="!!errors['last_name']"
            :error-message="errors['last_name'] ? errors['last_name'][0] : null"
            outlined
            placeholder="Last Name"
            dense
            hide-bottom-space
          />
        </div>
      </div>
    </detail-item>

    <!-- Email -->
    <detail-item
      :name-spacing="nameSpacing"
      with-input
      icon="mdi-email"
      name="Email Address"
      required
    >
      <email-address
        v-model="user.email"
        :error="!!errors['email']"
        :error-message="errors['email'] ? errors['email'][0] : null"
        outlined
        dense
        hide-bottom-space
        placeholder="Email Address"
      />
      <page-banner
        v-if="!editMode || hasChangedEmail || user.unverified_email"
        dense
        class="q-mt-xs"
      >
        <q-item-label v-if="!editMode">
          The user will need to verify this email address.
        </q-item-label>

        <template v-else-if="hasChangedEmail">
          <q-item-label>
            The current email address <b>{{ oldEmail }}</b> will remain active until the new email address has been verified.
          </q-item-label>
          <q-item-label>
            The user will have <b>24 hours</b> to verify your new email address, otherwise your attempt to change it will be cancelled.
          </q-item-label>
        </template>

        <q-item-label v-else>
          A pending request exists to change their email to <b>{{ user.unverified_email.email }}</b>. Changing their email again will override this existing request.
        </q-item-label>
      </page-banner>
    </detail-item>

    <!-- Phone -->
    <detail-item
      :name-spacing="nameSpacing"
      :with-input="!user.phone ? 31 : true"
      icon="mdi-phone"
      name="Contact Number"
    >
      <phone-number
        :phone="user.phone"
        :error="!!errors['phone']"
        outline
        clearable
        dense
        hide-bottom-space
        @create="addPhone"
        @clear="user.phone = null"
      />
    </detail-item>

    <!-- Password -->
    <detail-item
      v-if="!editMode"
      :name-spacing="nameSpacing"
      with-input
      icon="mdi-form-textbox-password"
      name="Password"
      required
    >
      <q-checkbox
        v-model="user.with_password"
        label="Enter password for new user?"
        class="q-mb-xs"
      />

      <div
        v-if="user.with_password"
        class="row q-col-gutter-sm"
      >
        <div class="col-md-6 col-12">
          <q-password
            v-model="user.password"
            :error="!!errors['password']"
            :error-message="errors['password'] ? errors['password'][0] : null"
            show-toggle
            outlined
            placeholder="Password"
            dense
            hide-bottom-space
          />
        </div>
        <div class="col-md-6 col-12">
          <q-password
            v-model="user.password_confirmation"
            outlined
            placeholder="Confirm Password"
            dense
          />
        </div>
      </div>

      <PasswordRequirements
        v-if="user.with_password"
        :password="user.password"
        class="q-mt-sm"
      />

      <page-banner
        v-else
        dense
        label="This user will be sent an email asking them to reset their password."
      />
    </detail-item>

    <!-- Colour -->
    <detail-item
      :name-spacing="nameSpacing"
      with-input
      icon="mdi-format-color-fill"
      name="Profile Colour"
    >
      <q-item-label>
        <color-field
          v-model="user.color"
          :error-message="!!errors['color'] ? errors['color'][0] : null"
        />
      </q-item-label>
    </detail-item>

    <!-- Signature -->
    <detail-item
      icon="mdi-signature-freehand"
      name="Signature"
      :with-input="34"
      :name-spacing="nameSpacing"
    >
      <page-banner
        dense
        color="blue-grey-1"
        class="q-mb-sm"
      >
        <q-item-label
          caption
        >
          This signature is used on documents which prefill a staff signature.
          <span v-if="editMode" class="text-bold text-red">
            Updating their signature will not affect existing reports.
          </span>
        </q-item-label>
      </page-banner>

      <signature-pad
        v-model="user.signature"
        :error-message="errors['signature'] ? errors['signature'][0] : null"
        label="Transcribe signature"
      />
    </detail-item>

    <file-select-dialog
      ref="fileSelectDialog"
      :selected="user.image"
      :preview-mode="false"
      restrict-to-type="IMAGE"
      @saved="(file) => user.image = file"
      @deleted="user.image = null"
    />
  </q-list>
</template>

<script>
import PasswordRequirements from 'components/partials/PasswordRequirements'
import { getPhonePayload } from 'components/partials/PhoneAddressEmail'

export default {
  components: {
    PasswordRequirements
  },
  props: {
    user: {
      type: Object,
      required: true
    },
    oldEmail: {
      type: String
    },
    hasChangedEmail: {
      type: Boolean,
      default: false
    },
    errors: {
      type: Object,
      default: () => {
        return {}
      }
    },
    nameSpacing: {
      type: Number,
      required: true
    },
    editMode: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    initials () {
      if (this.user.first_name.length < 1 || this.user.last_name.length < 1) {
        return '#'
      }
      const first = this.user.first_name[0]
      const last = this.user.last_name[0]
      return first + last
    }
  },
  methods: {
    addPhone () {
      this.user.phone = getPhonePayload()
    }
  }
}
</script>
